/**
 * skyEmployee access vocabulary: roles (mapped from the platform roles),
 * capabilities, scope and audience shapes.
 *
 * Imported by BOTH the server (actor resolver, policy, route guards) and the
 * client (`useEmployeeAccess`, nav). `ROLE_CAPABILITIES` is the whole
 * permission spec: screens and routes test capabilities, never role names, so
 * a permission change is a change to this table and to its matrix test
 * (`access.test.ts`) in the same PR. The table mirrors §1.3 of
 * `docs/superpowers/plans/2026-09-28-skyemployee-roadmap.md`.
 *
 * Capabilities for features that do not exist yet are declared now on
 * purpose: the matrix test covers them before the feature lands, so a later
 * phase only has to consume them.
 */

import type { Role } from "../roles";
import type { BroadcastAccess } from "./broadcastAccess";

export const EMP_ROLES = ["admin", "controller", "employee"] as const;
export type EmpRole = (typeof EMP_ROLES)[number];

export function isEmpRole(value: unknown): value is EmpRole {
  return typeof value === "string" && (EMP_ROLES as readonly string[]).includes(value);
}

/**
 * skyEmployee has NO roles of its own. A person's skyEmployee role is their
 * platform role (user_role_assignments, assigned in Role Management), read
 * through this table:
 *
 *   skypoint_admin     → admin       (also the only role that switches the product on/off)
 *   skysupport_admin   → admin
 *   skysupport_manager → controller
 *   skysupport_user    → employee
 *
 * A manager therefore sees Service Desk and skyEmployee but not Workforce
 * Lifecycle, whose gate stays admin-only.
 */
export const PLATFORM_ROLE_TO_EMP_ROLE: Readonly<Record<Role, EmpRole>> = {
  skypoint_admin: "admin",
  skysupport_admin: "admin",
  skysupport_manager: "controller",
  skysupport_user: "employee",
};

/** Every Access Management role, in order of seniority. */
export const PLATFORM_ROLES = Object.keys(PLATFORM_ROLE_TO_EMP_ROLE) as readonly Role[];

/**
 * How skyEmployee names each Access Management role. Skypoint Admin keeps its
 * own name; the skySupport roles read as Admin / Manager / User.
 */
export const PLATFORM_ROLE_LABELS: Readonly<Record<Role, string>> = {
  skypoint_admin: "Skypoint Admin",
  skysupport_admin: "Admin",
  skysupport_manager: "Manager",
  skysupport_user: "User",
};

/** The skyEmployee role for a platform role; null when there is none (no access). */
export function empRoleFromPlatformRole(role: string | null | undefined): EmpRole | null {
  return role && Object.prototype.hasOwnProperty.call(PLATFORM_ROLE_TO_EMP_ROLE, role)
    ? PLATFORM_ROLE_TO_EMP_ROLE[role as Role]
    : null;
}

export const EMP_CAPABILITIES = [
  // Everyone
  "feed.post",
  "ecards.send",
  // Managers (Controllers inside their scope, Admins everywhere)
  "ecards.managerDesigns",
  "content.publish",
  "content.target",
  "content.moderate",
  "banners.manage",
  "giftcards.award",
  "fulfilment.manage",
  "acks.track",
  "polls.create",
  "surveys.results",
  "surveys.export",
  // Controller-only
  "giftcards.allowance",
  "nav.manage",
  // Admin-only
  "videos.manage",
  "giftcards.admin",
  "library.manage",
  "surveys.build",
  "messages.reviewReports",
  "people.manage",
  "org.manage",
  "recognition.admin",
  "settings.manage",
  "exports.run",
  "nav.admin",
] as const;
export type EmpCapability = (typeof EMP_CAPABILITIES)[number];

export function isEmpCapability(value: unknown): value is EmpCapability {
  return typeof value === "string" && (EMP_CAPABILITIES as readonly string[]).includes(value);
}

const EVERYONE: readonly EmpCapability[] = ["feed.post", "ecards.send"];

const MANAGER: readonly EmpCapability[] = [
  "ecards.managerDesigns",
  "content.publish",
  "content.target",
  "content.moderate",
  "banners.manage",
  "giftcards.award",
  "fulfilment.manage",
  "acks.track",
  "polls.create",
  "surveys.results",
  "surveys.export",
];

const ADMIN_ONLY: readonly EmpCapability[] = [
  "videos.manage",
  "giftcards.admin",
  "library.manage",
  "surveys.build",
  "messages.reviewReports",
  "people.manage",
  "org.manage",
  "recognition.admin",
  "settings.manage",
  "exports.run",
  "nav.admin",
];

/**
 * Capabilities a Controller holds only inside their scope. Holding one of
 * these says nothing about WHICH rows the caller may touch: the server must
 * also apply `policy.inScope` / `scopeSql`. Admins hold them everywhere.
 */
export const SCOPED_CAPABILITIES: ReadonlySet<EmpCapability> = new Set<EmpCapability>([
  "content.publish",
  "content.target",
  "content.moderate",
  "banners.manage",
  "giftcards.award",
  "fulfilment.manage",
  "acks.track",
  "polls.create",
  "surveys.results",
  "surveys.export",
]);

export const ROLE_CAPABILITIES: Readonly<Record<EmpRole, readonly EmpCapability[]>> = {
  employee: EVERYONE,
  // The allowance meter applies to Controllers only; the "Manage · <location>"
  // menu section is the Controller's, Admins get "Administration" instead.
  controller: [...EVERYONE, ...MANAGER, "giftcards.allowance", "nav.manage"],
  admin: [...EVERYONE, ...MANAGER, ...ADMIN_ONLY],
};

export function hasCapability(caps: readonly EmpCapability[], cap: EmpCapability): boolean {
  return caps.includes(cap);
}

/** A scope entry: a whole location (`departmentId` null) or one team at a location. */
export interface EmpScopeEntry {
  locationId: string;
  departmentId: string | null;
}

/** Admin → `all`; Controller → their entries; Employee → no entries. */
export type EmpScope = { kind: "all" } | { kind: "entries"; entries: EmpScopeEntry[] };

/**
 * Who a piece of content is aimed at. A `teams` entry with a null
 * `departmentId` means the whole location, so a Controller's mixed scope is a
 * single `teams` audience and teams added to a location later are still
 * included.
 */
export type EmpAudience =
  | { kind: "everyone" }
  | { kind: "locations"; locationIds: string[] }
  | { kind: "teams"; teams: EmpScopeEntry[] }
  | { kind: "tenure"; maxDays: number };

/** Work details only. Never personal contact fields. */
export interface EmpMePerson {
  name: string;
  jobTitle: string | null;
  locationId: string | null;
  locationName: string | null;
  departmentId: string | null;
  departmentName: string | null;
}

/** Response of `GET /api/v1/employee/me`. */
export interface EmpMe {
  role: EmpRole;
  /** The Access Management role the skyEmployee role comes from. */
  platformRole: Role;
  partyId: string | null;
  person: EmpMePerson | null;
  scope: EmpScope;
  /** Locations offered by Manage-screen switchers. Admin: every active location. */
  scopeLocations: { id: string; name: string }[];
  capabilities: EmpCapability[];
  /**
   * What they may do with broadcasts: their role's row of the organisation's
   * Who can do what matrix (shared/employee/broadcastAccess.ts). Absent from
   * an older server: treat as no access.
   */
  broadcast?: BroadcastAccess;
  /** Messages between colleagues is on (Messaging & broadcasts → Chats). Absent: on. */
  messagesOn?: boolean;
}

/**
 * Response of `GET /api/v1/employee/me/profile`: the caller's own directory
 * record, as the HR system / Entra sync wrote it. Work fields only — never
 * phone, address, date of birth or emergency contacts.
 */
export interface EmpMyProfile {
  name: string;
  email: string;
  jobTitle: string | null;
  departmentName: string | null;
  /** The office location exactly as written in the directory. */
  officeLocation: string | null;
  /** The skyEmployee location (may be set by an admin, overriding the directory). */
  locationName: string | null;
  manager: { name: string; email: string } | null;
  /** "inactive" when the account is disabled in the directory. */
  accountStatus: "active" | "inactive";
  /** The person's own photo endpoint, or null when they haven't set one. */
  photoUrl: string | null;
  /** Start of the primary job (YYYY-MM-DD); null when unknown. */
  startDate: string | null;
}

/** One location or team a Manager looks after, with display names. */
export interface EmpMyScopeEntry {
  locationId: string;
  locationName: string;
  /** null = the whole location. */
  departmentId: string | null;
  departmentName: string | null;
}

/** Error codes the employee API returns in `{ error, code }` bodies. */
export const EMP_ERROR_CODES = {
  DISABLED: "EMPLOYEE_DISABLED",
  NOT_AN_EMPLOYEE: "NOT_AN_EMPLOYEE",
  FORBIDDEN_CAPABILITY: "FORBIDDEN_CAPABILITY",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION: "VALIDATION_ERROR",
  LAST_ADMIN: "LAST_ADMIN",
  SELF_DEACTIVATE: "SELF_DEACTIVATE",
  NOT_A_CONTROLLER: "NOT_A_CONTROLLER",
  LOCATION_IN_USE: "LOCATION_IN_USE",
  /** A leaver (every employment ended) can't be placed until rehired. */
  EMPLOYMENT_ENDED: "EMPLOYMENT_ENDED",
  STORAGE_UNAVAILABLE: "STORAGE_UNAVAILABLE",
  NO_AGENT_RECORD: "NO_AGENT_RECORD",
  /** EMP_CODE_KEY isn't configured, so gift card codes can't be loaded or revealed. */
  GIFT_CODES_NOT_SET_UP: "GIFT_CODES_NOT_SET_UP",
  /** An award would go over the awarder's allowance or a budget for the quarter. */
  GIFT_LIMIT_REACHED: "GIFT_LIMIT_REACHED",
  /** Not enough codes (or stock) left for an award. */
  GIFT_OUT_OF_STOCK: "GIFT_OUT_OF_STOCK",
  /** Someone else saved these settings since they were loaded. */
  SETTINGS_CHANGED: "SETTINGS_CHANGED",
  /** A broadcast to more people than the confirmation size: send again with the count to confirm. */
  CONFIRM_SEND: "CONFIRM_SEND",
} as const;
export type EmpErrorCode = (typeof EMP_ERROR_CODES)[keyof typeof EMP_ERROR_CODES];

/** A location's key ("loc") or a team's ("loc:dept"), as audience pickers use them. */
export function audienceKey(locationId: string, departmentId: string | null): string {
  return departmentId ? `${locationId}:${departmentId}` : locationId;
}

/**
 * What a narrower audience may be chosen from within `outer`: everything (for
 * everyone), its whole locations, its single teams ("loc:dept"), or new
 * starters up to its number of days. One place for the rule, so a picker
 * limited to an audience offers exactly what audienceWithin accepts.
 */
export interface AudienceAllowance {
  everyone: boolean;
  wholeLocations: Set<string>;
  teams: Set<string>;
  maxDays: number | null;
}

export function audienceAllowance(outer: EmpAudience): AudienceAllowance {
  return {
    everyone: outer.kind === "everyone",
    wholeLocations: new Set(
      outer.kind === "locations"
        ? outer.locationIds
        : outer.kind === "teams"
          ? outer.teams.filter((t) => !t.departmentId).map((t) => t.locationId)
          : [],
    ),
    teams: new Set(
      outer.kind === "teams"
        ? outer.teams
            .filter((t) => t.departmentId)
            .map((t) => audienceKey(t.locationId, t.departmentId))
        : [],
    ),
    maxDays: outer.kind === "tenure" ? outer.maxDays : null,
  };
}

/**
 * Whether `inner` reaches only people `outer` reaches: everyone covers
 * anything; chosen locations cover themselves and their teams; chosen teams
 * cover only those teams; new starters cover new starters over the same or
 * fewer days.
 */
export function audienceWithin(inner: EmpAudience, outer: EmpAudience): boolean {
  const allow = audienceAllowance(outer);
  if (allow.everyone) return true;
  if (allow.maxDays !== null) return inner.kind === "tenure" && inner.maxDays <= allow.maxDays;
  if (inner.kind === "locations") {
    return (
      inner.locationIds.length > 0 && inner.locationIds.every((l) => allow.wholeLocations.has(l))
    );
  }
  if (inner.kind === "teams") {
    return (
      inner.teams.length > 0 &&
      inner.teams.every(
        (t) =>
          allow.wholeLocations.has(t.locationId) ||
          (!!t.departmentId && allow.teams.has(audienceKey(t.locationId, t.departmentId))),
      )
    );
  }
  return false;
}
